import { useState, useEffect, useCallback } from 'react';
import type { OrchestratorStatus, TaskDraft, OrchestrateMessage, Action } from '@/types/chat.types';

/**
 * Session Storage Keys
 */
const PENDING_SESSION_KEY = 'studyjoyful_pending_session';

/**
 * Current user ID for session validation
 * Set by AuthContext when user logs in
 */
let currentUserId: string | null = null;

/**
 * Set current user ID - called by AuthContext
 */
export function setSessionUserId(userId: string | null): void {
  currentUserId = userId;
}

/**
 * Get current user ID
 */
export function getSessionUserId(): string | null {
  return currentUserId;
}

/**
 * Pending Session Data
 */
export interface PendingSession {
  conversationId: string;
  status: OrchestratorStatus | string;
  taskDraft?: TaskDraft | null;
  messages: OrchestrateMessage[];
  /** Actions from the last assistant message */
  actions?: Action[];
  savedAt: string;
  /** User ID who created this session (for cross-user validation) */
  userId?: string;
}

/**
 * Save pending session to localStorage
 */
export function savePendingSession(session: Omit<PendingSession, 'savedAt' | 'userId'>): void {
  try {
    const data: PendingSession = {
      ...session,
      savedAt: new Date().toISOString(),
      userId: currentUserId || undefined,
    };
    localStorage.setItem(PENDING_SESSION_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('[SessionStorage] Failed to save session:', e);
  }
}

/**
 * Load pending session from localStorage
 * Only returns session if it belongs to the current user
 */
export function loadPendingSession(): PendingSession | null {
  try {
    const data = localStorage.getItem(PENDING_SESSION_KEY);
    if (!data) return null;

    const session = JSON.parse(data) as PendingSession;

    // Check if session is too old (24 hours)
    const savedAt = new Date(session.savedAt);
    const now = new Date();
    const hoursDiff = (now.getTime() - savedAt.getTime()) / (1000 * 60 * 60);
    if (hoursDiff > 24) {
      clearPendingSession();
      return null;
    }

    // Don't recover completed or error sessions
    if (session.status === 'complete' || session.status === 'error') {
      clearPendingSession();
      return null;
    }

    // Validate user ID - don't recover sessions from different users
    if (session.userId && currentUserId && session.userId !== currentUserId) {
      console.log('[SessionStorage] Session belongs to different user, clearing');
      clearPendingSession();
      return null;
    }

    return session;
  } catch (e) {
    console.warn('[SessionStorage] Failed to load session:', e);
    return null;
  }
}

/**
 * Clear pending session from localStorage
 */
export function clearPendingSession(): void {
  try {
    localStorage.removeItem(PENDING_SESSION_KEY);
  } catch (e) {
    console.warn('[SessionStorage] Failed to clear session:', e);
  }
}

/**
 * ============================================
 * useSessionRecovery Hook
 * ============================================
 *
 * 检查并恢复未完成的会话。
 */

export interface UseSessionRecoveryReturn {
  /** Whether checking for pending session */
  isChecking: boolean;
  /** Pending session if found */
  pendingSession: PendingSession | null;
  /** Whether to show recovery modal */
  showRecoveryModal: boolean;
  /** Continue with the pending session */
  continueSession: () => void;
  /** Discard the pending session */
  discardSession: () => Promise<void>;
  /** Clear session state (called when task is completed) */
  clearSession: () => void;
}

export function useSessionRecovery(): UseSessionRecoveryReturn {
  const [isChecking, setIsChecking] = useState(true);
  const [pendingSession, setPendingSession] = useState<PendingSession | null>(null);
  const [showRecoveryModal, setShowRecoveryModal] = useState(false);

  // Check for pending session on mount
  useEffect(() => {
    const session = loadPendingSession();
    if (session && session.messages.length > 0) {
      setPendingSession(session);
      setShowRecoveryModal(true);
    }
    setIsChecking(false);
  }, []);

  const continueSession = useCallback(() => {
    setShowRecoveryModal(false);
  }, []);

  const discardSession = useCallback(async () => {
    clearPendingSession();
    setPendingSession(null);
    setShowRecoveryModal(false);
  }, []);

  // Clear session state when task is completed
  // This ensures the recovery modal won't show for completed tasks
  const clearSession = useCallback(() => {
    clearPendingSession();
    setPendingSession(null);
    setShowRecoveryModal(false);
  }, []);

  return {
    isChecking,
    pendingSession,
    showRecoveryModal,
    continueSession,
    discardSession,
    clearSession,
  };
}

export default useSessionRecovery;
